
import {computed, defineComponent, onMounted, reactive, ref} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import {navigate} from '@/core/util/helper/navigation';

type Employee = {
  empNumber: number;
  employeeId: string;
  firstName: string;
  lastName: string;
};
type PayComponent = {
  id: number;
  name: string;
  type: 'earning' | 'deduction';
  calc_type: 'fixed' | 'percent' | 'commission';
  is_taxable: number;
  is_active: number;
};
type SalaryStructure = {
  id: number;
  emp_number: number;
  employee_id: string;
  first_name: string;
  last_name: string;
  basic_salary: string;
  effective_from: string;
  effective_to: string | null;
  used_in_payroll: number | boolean;
  components: Array<{
    name: string;
    type: string;
    calc_type: string;
    amount: string | null;
    percent: string | null;
  }>;
};
type PayrollRun = {
  id: number;
  period_start: string;
  period_end: string;
  status: string;
  employee_count: number;
  total_gross: string;
  total_net: string;
  entries?: Array<Record<string, any>>;
  missingEmployees?: Array<{
    emp_number: number;
    employee_id: string;
    first_name: string;
    last_name: string;
  }>;
};
type Payslip = {
  id: number;
  published_at: string | null;
  employee_id: string;
  first_name: string;
  last_name: string;
  period_start: string;
  period_end: string;
  net: string;
};
type TaxSlab = {upTo: number; baseTax: number; rate: number};

export default defineComponent({
  name: 'FinancePayrollWorkspace',
  setup() {
    const baseUrl = (window as unknown as {appGlobal: {baseUrl: string}})
      .appGlobal.baseUrl;
    const componentApi = new APIService(
      baseUrl,
      '/api/v2/finance/pay-components',
    );
    const structureApi = new APIService(
      baseUrl,
      '/api/v2/finance/salary-structures',
    );
    const employeeApi = new APIService(baseUrl, '/api/v2/pim/employees');
    const runApi = new APIService(baseUrl, '/api/v2/finance/payroll-runs');
    const settingsApi = new APIService(baseUrl, '/api/v2/finance/settings');
    const payslipApi = new APIService(baseUrl, '/api/v2/finance/payslips');
    const tab = ref<
      'structures' | 'components' | 'run' | 'history' | 'payslips' | 'settings'
    >('structures');
    const employees = ref<Employee[]>([]);
    const employeeSearch = ref('');
    const components = ref<PayComponent[]>([]);
    const structures = ref<SalaryStructure[]>([]);
    const runs = ref<PayrollRun[]>([]);
    const payslips = ref<Payslip[]>([]);
    const selectedEmployeeNumbers = ref<number[]>([]);
    const filteredEmployees = computed(() => {
      const query = employeeSearch.value.trim().toLocaleLowerCase();
      if (!query) return employees.value;
      return employees.value.filter((employee) =>
        `${employee.firstName} ${employee.lastName} ${employee.employeeId}`
          .toLocaleLowerCase()
          .includes(query),
      );
    });
    const currentRun = ref<PayrollRun | null>(null);
    const firstDay = new Date();
    firstDay.setDate(1);
    const lastDay = new Date(
      firstDay.getFullYear(),
      firstDay.getMonth() + 1,
      0,
    );
    const periodStart = ref(firstDay.toISOString().slice(0, 10));
    const periodEnd = ref(lastDay.toISOString().slice(0, 10));
    const selectedComponents = reactive<Record<number, boolean>>({});
    const componentValues = reactive<Record<number, number>>({});
    const saving = ref(false);
    const errorMessage = ref('');
    const form = reactive({
      empNumber: 0,
      effectiveFrom: new Date().toISOString().slice(0, 10),
      basicSalary: 0,
      paymentMode: 'bank_transfer',
      bankName: '',
      bankAccount: '',
    });
    const componentForm = reactive({
      name: '',
      type: 'earning',
      calcType: 'fixed',
      isTaxable: false,
    });
    const settings = reactive({
      currency: 'PKR',
      usd_pkr_rate: 0,
      pay_frequency: 'monthly',
      working_days_per_month: 22,
      overtime_multiplier: 1.5,
      pay_day: 1,
      tax_year_start: 7,
      payslip_footer: '',
      tax_slabs: [] as TaxSlab[],
    });
    const activeComponents = computed(() =>
      components.value.filter(
        (item) =>
          Number(item.is_active) &&
          !['basic salary', 'unpaid leave'].includes(
            item.name.trim().toLowerCase(),
          ),
      ),
    );
    const componentAmount = (item: PayComponent) => {
      const value = Number(componentValues[item.id] || 0);
      return item.calc_type === 'percent'
        ? (Number(form.basicSalary || 0) * value) / 100
        : item.calc_type === 'commission'
        ? 0
        : value;
    };
    const gross = computed(
      () =>
        Number(form.basicSalary || 0) +
        activeComponents.value
          .filter(
            (item) => item.type === 'earning' && selectedComponents[item.id],
          )
          .reduce((sum, item) => sum + componentAmount(item), 0),
    );
    const deductions = computed(() =>
      activeComponents.value
        .filter(
          (item) => item.type === 'deduction' && selectedComponents[item.id],
        )
        .reduce((sum, item) => sum + componentAmount(item), 0),
    );
    const money = (value: string | number) => Number(value || 0).toFixed(2);
    const structureTotal = (item: SalaryStructure, type: string) => {
      const componentSum = item.components
        .filter(
          (part) =>
            part.type === type &&
            !['basic salary', 'unpaid leave'].includes(
              part.name.trim().toLowerCase(),
            ),
        )
        .reduce(
          (sum, part) =>
            sum +
            Number(
              part.amount ||
                (Number(item.basic_salary) * Number(part.percent || 0)) / 100,
            ),
          0,
        );
      return money(
        type === 'earning'
          ? Number(item.basic_salary) + componentSum
          : componentSum,
      );
    };
    const message = (error: any) =>
      error?.response?.data?.error?.message || 'Finance request failed.';
    const load = async () => {
      errorMessage.value = '';
      try {
        const [
          employeeResponse,
          componentResponse,
          structureResponse,
          runResponse,
          settingsResponse,
        ] = await Promise.all([
          employeeApi.getAll({
            model: 'detailed',
            includeEmployees: 'onlyCurrent',
            limit: 200,
          }),
          componentApi.getAll(),
          structureApi.getAll({limit: 200}),
          runApi.getAll({limit: 50}),
          settingsApi.getAll(),
        ]);
        employees.value = employeeResponse.data.data || [];
        components.value = componentResponse.data.data || [];
        structures.value = structureResponse.data.data || [];
        runs.value = runResponse.data.data || [];
        if (tab.value === 'payslips') {
          const payslipResponse = await payslipApi.getAll({limit: 200});
          payslips.value = payslipResponse.data.data || [];
        }
        Object.assign(settings, settingsResponse.data.data?.[0] || {});
      } catch (error: any) {
        errorMessage.value = message(error);
      }
    };
    const saveStructure = async () => {
      saving.value = true;
      errorMessage.value = '';
      try {
        if (!Number.isInteger(Number(form.empNumber)) || form.empNumber < 1) {
          errorMessage.value =
            'Select an employee before saving the salary structure.';
          return;
        }
        const currentStructure = structures.value.find(
          (item) =>
            Number(item.emp_number) === Number(form.empNumber) &&
            !item.effective_to,
        );
        if (
          currentStructure &&
          form.effectiveFrom <= currentStructure.effective_from
        ) {
          errorMessage.value = `The new structure must start after ${currentStructure.effective_from}.`;
          return;
        }
        const selected = activeComponents.value
          .filter((item) => selectedComponents[item.id])
          .map((item) => ({
            componentId: item.id,
            [item.calc_type === 'fixed' ? 'amount' : 'percent']: Number(
              componentValues[item.id] || 0,
            ),
          }));
        await structureApi.create({...form, components: selected});
        form.basicSalary = 0;
        form.bankName = '';
        form.bankAccount = '';
        await load();
      } catch (error: any) {
        errorMessage.value = message(error);
      } finally {
        saving.value = false;
      }
    };
    const deleteStructure = async (id: number) => {
      if (
        !window.confirm(
          'Delete this salary structure version? Versions used by payroll history cannot be deleted.',
        )
      ) {
        return;
      }
      saving.value = true;
      errorMessage.value = '';
      try {
        await structureApi.delete(id);
        await load();
      } catch (error: any) {
        errorMessage.value = message(error);
      } finally {
        saving.value = false;
      }
    };
    const saveComponent = async () => {
      saving.value = true;
      errorMessage.value = '';
      try {
        await componentApi.create({...componentForm});
        componentForm.name = '';
        componentForm.isTaxable = false;
        await load();
      } catch (error: any) {
        errorMessage.value = message(error);
      } finally {
        saving.value = false;
      }
    };
    const deactivateComponent = async (item: PayComponent) => {
      errorMessage.value = '';
      try {
        await componentApi.update(item.id, {
          name: item.name,
          type: item.type,
          calcType: item.calc_type,
          isTaxable: Number(item.is_taxable),
          isActive: false,
        });
        await load();
      } catch (error: any) {
        errorMessage.value = message(error);
      }
    };
    const createRun = async () => {
      saving.value = true;
      errorMessage.value = '';
      try {
        const periodRuns = runs.value.filter(
          (run) =>
            run.period_start === periodStart.value &&
            run.period_end === periodEnd.value,
        );
        const existingRun =
          periodRuns.find((run) => run.status === 'draft') ||
          periodRuns.find((run) => ['approved', 'paid'].includes(run.status));
        if (existingRun) {
          if (existingRun.status === 'draft') {
            const response = await runApi.get(existingRun.id);
            currentRun.value = response.data.data as PayrollRun;
            errorMessage.value = `Run #${existingRun.id} is already a draft for this period. Select any additional employees above, then add them to this draft run below.`;
            return;
          }
          if (['approved', 'paid'].includes(existingRun.status)) {
            if (selectedEmployeeNumbers.value.length === 0) {
              errorMessage.value =
                'Select the employee or employees to include in a supplemental run for this approved period.';
              return;
            }
            const employeesWithoutStructure =
              selectedEmployeeNumbers.value.filter(
                (empNumber) =>
                  !structures.value.some(
                    (structure) =>
                      Number(structure.emp_number) === empNumber &&
                      structure.effective_from <= periodEnd.value &&
                      (!structure.effective_to ||
                        structure.effective_to >= periodEnd.value),
                  ),
              );
            if (employeesWithoutStructure.length > 0) {
              errorMessage.value =
                'The selected employees need a salary structure effective on the payroll period end date.';
              return;
            }
            const response = await runApi.create({
              periodStart: periodStart.value,
              periodEnd: periodEnd.value,
              employeeNumbers: selectedEmployeeNumbers.value,
              supplemental: true,
            });
            currentRun.value = response.data.data as PayrollRun;
            selectedEmployeeNumbers.value = [];
            tab.value = 'run';
            await load();
            errorMessage.value =
              'Supplemental draft created. Review the employee and pay amounts before approval.';
            return;
          }
        }
        if (selectedEmployeeNumbers.value.length > 0) {
          const employeesWithoutStructure =
            selectedEmployeeNumbers.value.filter(
              (empNumber) =>
                !structures.value.some(
                  (structure) =>
                    Number(structure.emp_number) === empNumber &&
                    structure.effective_from <= periodEnd.value &&
                    (!structure.effective_to ||
                      structure.effective_to >= periodEnd.value),
                ),
            );
          if (employeesWithoutStructure.length > 0) {
            errorMessage.value =
              'The selected employees need a salary structure effective on the payroll period end date.';
            return;
          }
        }
        const response = await runApi.create({
          periodStart: periodStart.value,
          periodEnd: periodEnd.value,
          employeeNumbers: selectedEmployeeNumbers.value,
        });
        currentRun.value = response.data.data;
        tab.value = 'run';
        await load();
      } catch (error: any) {
        errorMessage.value = message(error);
      } finally {
        saving.value = false;
      }
    };
    const openRun = async (id: number) => {
      errorMessage.value = '';
      try {
        const response = await runApi.get(id);
        const selectedRun = response.data.data as PayrollRun;
        currentRun.value = selectedRun;
        periodStart.value = selectedRun.period_start;
        periodEnd.value = selectedRun.period_end;
        tab.value = 'run';
      } catch (error: any) {
        errorMessage.value = message(error);
      }
    };
    const deleteRun = async (id: number) => {
      if (
        !window.confirm(
          'Delete this draft payroll run and its draft entries? Approved and paid payroll history cannot be deleted.',
        )
      ) {
        return;
      }
      saving.value = true;
      errorMessage.value = '';
      try {
        await runApi.delete(id);
        if (Number(currentRun.value?.id) === id) currentRun.value = null;
        await load();
      } catch (error: any) {
        errorMessage.value = message(error);
      } finally {
        saving.value = false;
      }
    };
    const addEmployeesToRun = async () => {
      if (!currentRun.value || currentRun.value.status !== 'draft') return;
      saving.value = true;
      errorMessage.value = '';
      try {
        if (selectedEmployeeNumbers.value.length > 0) {
          const withoutStructure = selectedEmployeeNumbers.value.filter(
            (empNumber) =>
              !structures.value.some(
                (structure) =>
                  Number(structure.emp_number) === empNumber &&
                  structure.effective_from <= currentRun.value!.period_end &&
                  (!structure.effective_to ||
                    structure.effective_to >= currentRun.value!.period_end),
              ),
          );
          if (withoutStructure.length > 0) {
            errorMessage.value =
              'The selected employees need a salary structure effective on the payroll period end date.';
            return;
          }
        }
        const response = await runApi.update(currentRun.value.id, {
          action: 'addEmployees',
          employeeNumbers: selectedEmployeeNumbers.value,
        });
        currentRun.value = response.data.data as PayrollRun;
        selectedEmployeeNumbers.value = [];
        runs.value = runs.value.map((run) =>
          Number(run.id) === Number(currentRun.value?.id)
            ? {
                ...run,
                employee_count:
                  currentRun.value?.employee_count ?? run.employee_count,
                total_gross: currentRun.value?.total_gross ?? run.total_gross,
                total_net: currentRun.value?.total_net ?? run.total_net,
              }
            : run,
        );
      } catch (error: any) {
        errorMessage.value = message(error);
      } finally {
        saving.value = false;
      }
    };
    const recalculateRun = async () => {
      if (!currentRun.value?.entries) return;
      saving.value = true;
      errorMessage.value = '';
      try {
        const response = await runApi.update(currentRun.value.id, {
          action: 'recalculate',
          entries: currentRun.value.entries.map((entry) => ({
            entryId: Number(entry.id),
            workingDays: Number(entry.working_days),
            leaveDays: Number(entry.leave_days),
            overtimeHours: Number(entry.overtime_hours),
            salesRevenueUsd: Number(entry.sales_revenue_usd || 0),
            bonus: Number(entry.bonus || 0),
            loanInstallment: Number(entry.loanInstallment || 0),
          })),
        });
        currentRun.value = response.data.data;
        await load();
      } catch (error: any) {
        errorMessage.value = message(error);
      } finally {
        saving.value = false;
      }
    };
    const runAction = async (action: string) => {
      if (!currentRun.value) return;
      saving.value = true;
      errorMessage.value = '';
      try {
        const response = await runApi.update(currentRun.value.id, {action});
        currentRun.value = response.data.data;
        await load();
      } catch (error: any) {
        errorMessage.value = message(error);
      } finally {
        saving.value = false;
      }
    };
    const reverseRun = async (id: number) => {
      const reason = window.prompt(
        'Enter the reason for reversing this paid payroll run:',
      );
      if (!reason?.trim()) return;
      saving.value = true;
      errorMessage.value = '';
      try {
        await runApi.update(id, {action: 'reverse', reason: reason.trim()});
        await load();
      } catch (error: any) {
        errorMessage.value = message(error);
      } finally {
        saving.value = false;
      }
    };
    const downloadBankFile = (id: number) => {
      window.location.assign(`${baseUrl}/finance/payroll-runs/${id}/bank-file`);
    };
    const saveSettings = async () => {
      saving.value = true;
      errorMessage.value = '';
      try {
        await settingsApi.create({settings: {...settings}});
        await load();
      } catch (error: any) {
        errorMessage.value = message(error);
      } finally {
        saving.value = false;
      }
    };
    const openPayslips = async () => {
      tab.value = 'payslips';
      await load();
    };
    const togglePayslip = async (slip: Payslip) => {
      saving.value = true;
      errorMessage.value = '';
      try {
        await payslipApi.update(slip.id, {
          action: slip.published_at ? 'unpublish' : 'publish',
        });
        await load();
      } catch (error: any) {
        errorMessage.value = message(error);
      } finally {
        saving.value = false;
      }
    };
    onMounted(load);
    return {
      tab,
      employees,
      components,
      structures,
      runs,
      payslips,
      selectedEmployeeNumbers,
      employeeSearch,
      filteredEmployees,
      currentRun,
      periodStart,
      periodEnd,
      activeComponents,
      selectedComponents,
      componentValues,
      form,
      componentForm,
      settings,
      saving,
      errorMessage,
      gross,
      deductions,
      money,
      structureTotal,
      saveStructure,
      deleteStructure,
      saveComponent,
      deactivateComponent,
      createRun,
      addEmployeesToRun,
      openRun,
      deleteRun,
      recalculateRun,
      runAction,
      reverseRun,
      downloadBankFile,
      saveSettings,
      openPayslips,
      togglePayslip,
      navigate,
    };
  },
});
